import CloseIcon from '@material-design-icons/svg/outlined/close.svg?react';
import KeyboardDownArrowIcon from '@material-design-icons/svg/outlined/keyboard_arrow_down.svg?react';
import SearchIcon from '@material-design-icons/svg/outlined/search.svg?react';
import * as Popover from '@radix-ui/react-popover';
import { escapeRegExp } from 'es-toolkit/string';
import {
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
  type SetStateAction,
} from 'react';
import { twMerge } from 'tailwind-merge';

export interface Choice {
  label: string;
  key: string;
  value?: string;
}

interface SearchablePopoverShellState<C extends Choice> {
  filteredChoices: C[];
  highlightedIndex: number;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  setHighlightedIndex: (value: SetStateAction<number>) => void;
  itemRefs: MutableRefObject<(HTMLDivElement | null)[]>;
  listRef: RefObject<HTMLDivElement | null>;
  close: () => void;
  select: (choice: C) => void;
  highlightMatch: (text: string) => ReactNode;
}

interface SearchablePopoverShellProps<C extends Choice> {
  className?: string;
  disabled?: boolean;
  choices: C[];
  searchPlaceholder?: string;
  triggerContent: ReactNode;
  getFilteredChoices(choices: C[], searchTerm: string): C[];
  getHighlightTerms(searchTerm: string): string[];
  onHighlightError?(
    error: unknown,
    searchTerm: string,
    highlightTerms: string[],
  ): void;
  onSelect(
    choice: C,
    actions: {
      close: () => void;
      setSearchTerm: (value: string) => void;
      setHighlightedIndex: (value: SetStateAction<number>) => void;
    },
  ): void;
  children(state: SearchablePopoverShellState<C>): ReactNode;
}

function buildHighlightMatch(
  searchTerm: string,
  highlightTerms: string[],
  onHighlightError?: (
    error: unknown,
    searchTerm: string,
    highlightTerms: string[],
  ) => void,
): (text: string) => ReactNode {
  if (highlightTerms.length === 0) {
    return (text) => text;
  }

  return (text: string) => {
    if (!searchTerm) return text;

    try {
      const escapedTerms = highlightTerms.map((term) => escapeRegExp(term));
      const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
      const lowerCasedTerms = new Set(
        highlightTerms.map((term) => term.toLowerCase()),
      );
      const parts = text.split(regex);

      return (
        <>
          {parts.map((part, index) => {
            const isMatch = lowerCasedTerms.has(part.toLowerCase());

            return isMatch ? (
              <span key={index} className="bg-teal-600 text-white">
                {part}
              </span>
            ) : (
              <span key={index}>{part}</span>
            );
          })}
        </>
      );
    } catch (error) {
      onHighlightError?.(error, searchTerm, highlightTerms);
      return text;
    }
  };
}

export function SearchablePopoverShell<C extends Choice>({
  className,
  disabled,
  choices,
  searchPlaceholder,
  triggerContent,
  getFilteredChoices,
  getHighlightTerms,
  onHighlightError,
  onSelect,
  children,
}: SearchablePopoverShellProps<C>) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const filteredChoices = getFilteredChoices(choices, searchTerm);
  const highlightTerms = getHighlightTerms(searchTerm);
  const highlightMatch = buildHighlightMatch(
    searchTerm,
    highlightTerms,
    onHighlightError,
  );

  const close = () => {
    setOpen(false);
    setSearchTerm('');
    setHighlightedIndex(0);
  };

  const select = (choice: C) => {
    onSelect(choice, {
      close,
      setSearchTerm,
      setHighlightedIndex,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredChoices.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredChoices[highlightedIndex]) {
          select(filteredChoices[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
    }
  };

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    if (itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        disabled={disabled}
        className={twMerge(
          'grid-cols-[1fr_max-content]',
          'grid',
          'group',
          'h-10',
          'items-center',
          'outline-none',
          'px-4',
          'text-left',
          'text-sm',
          'text-white',
          'transition-colors',
          'w-full',
          className,
        )}
      >
        {triggerContent}
        <KeyboardDownArrowIcon
          className={twMerge(
            'fill-white transition-all',
            open && '-rotate-180',
          )}
        />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          onKeyDown={handleKeyDown}
          className="z-[999999] w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)]"
          style={{
            backgroundColor: 'rgb(38, 38, 38)',
            border: '1px solid rgb(64, 64, 64)',
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
          }}
        >
          <div className="p-2 border-b border-neutral-700">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 fill-neutral-400" />
              <input
                ref={searchInputRef}
                type="text"
                aria-label={searchPlaceholder || 'Search'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder || 'Search...'}
                className="w-full h-9 pl-10 pr-8 bg-neutral-800 border border-neutral-700 rounded-lg text-sm text-white placeholder-neutral-500 outline-none focus:border-neutral-500"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-700 rounded"
                >
                  <CloseIcon className="size-4 fill-neutral-400" />
                </button>
              )}
            </div>
          </div>

          {children({
            filteredChoices,
            highlightedIndex,
            searchTerm,
            setSearchTerm,
            setHighlightedIndex,
            itemRefs,
            listRef,
            close,
            select,
            highlightMatch,
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
